<template>
  <div class="contents">
    <div class="layout">

      <div class="page-top">
        <h2>월간 콘텐츠 관리</h2>
        <div class="breadcrumbs">
          <span>콘텐츠 관리</span>
          <span class="color-black">월간 콘텐츠 관리</span>
        </div>
      </div>

      <div class="white-panel mt30 pt30 pb30">
        <div class="search-panel">
          <table>
            <colgroup>
              <col style="width:150px;">
              <col style="width:150px;">
              <col>
            </colgroup>
            <tbody>
            <tr>
              <th>콘텐츠 타입</th>
              <td>
                <select v-model="filterContentType" @change="fetchItems">
                  <option value="">-- 모든 타입 --</option>
                  <option
                    v-for="option in contentTypeOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </td>
              <td></td>
            </tr>
            <tr>
              <th>공개 여부</th>
              <td>
                <select v-model="visibilityFilter">
                  <option value="">-- 전체 --</option>
                  <option value="public">공개 (이번 달 {{ formattedSelectedMonth }})</option>
                  <option value="private">비공개 (지난/다음 달)</option>
                </select>
              </td>
              <td></td>
            </tr>
            <tr>
              <th>검색어</th>
              <td>
                <div class="input-search">
                  <input v-model="searchQuery" @keyup.enter="fetchItems" type="text" name="search-input" placeholder="검색어 입력">
                  <i @click="fetchItems" class="button-search" style="cursor: pointer;"><img src="@/assets/images/ico/ico_search.svg" alt=""/></i>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="list-top mt50">
        <h3></h3>
        <div class="control-group">
          <div class="btn-group">
            <button @click="deleteItems" type="button" class="delete-button">삭제</button>
            <button @click="addContent" type="button" class="navy-button add-button">등록</button>
          </div>
        </div>
      </div>

      <div v-if="isDraggable" class="reorder-hint reorder-hint--on">
        맨 앞 칸의 손잡이 아이콘(점 6개)을 잡고 위아래로 드래그하면 공개순서가 바뀝니다. (이번 달 · {{ getContentTypeLabel(filterContentType) }})
      </div>
      <div v-else class="reorder-hint">
        ℹ 순서 변경은 <b>공개 여부 = 공개</b> + <b>콘텐츠 타입</b>을 함께 선택하면 활성화됩니다.
      </div>

      <div class="white-panel mt20">
        <div class="table-wrap">
          <div class="scroll-box">
            <table class="table-type1">
              <colgroup>
                <col style="width:60px;">
                <col style="width:120px;">
                <col style="width:100px;">
                <col>
                <col>
                <col>
                <col style="width:120px;">
                <col style="width:120px;">
              </colgroup>
              <thead>
              <tr>
                <th>
									<span v-if="!isDraggable" class="checkbox-type1">
										<input @click="toggleAllCheck" type="checkbox" id="allchk">
										<label for="allchk">체크</label>
									</span>
                </th>
                <th>관리 No.</th>
                <th>타입</th>
                <th>선정 년월</th>
                <th>콘텐츠 ID</th>
                <th>공개순서</th>
                <th>작성자</th>
                <th>등록일</th>
              </tr>
              </thead>
              <tbody>
              <tr
                v-for="(item, index) in displayItems"
                :key="item.id"
                :class="{
                  'bg-hidden': item.selectedMonth !== formattedSelectedMonth,
                  'drag-row': isDraggable,
                  'drag-over': isDraggable && overIndex === index,
                  'group-start': isGroupStart(item, index),
                }"
                @dragenter.prevent="isDraggable && onDragEnter(index, displayItems, groupOf)"
                @dragover.prevent
                @drop="isDraggable && onDrop(displayItems, groupOf, applyGroupOrder)"
                @dragend="onDragEnd"
              >
                <td>
                    <span
                      v-if="isDraggable"
                      class="drag-handle"
                      :draggable="true"
                      @dragstart="onDragStart(index)"
                      title="여기를 잡고 위아래로 드래그하세요"
                    >
                      <svg width="12" height="18" viewBox="0 0 12 18" fill="currentColor" aria-hidden="true">
                        <circle cx="4" cy="4" r="1.6" /><circle cx="8" cy="4" r="1.6" />
                        <circle cx="4" cy="9" r="1.6" /><circle cx="8" cy="9" r="1.6" />
                        <circle cx="4" cy="14" r="1.6" /><circle cx="8" cy="14" r="1.6" />
                      </svg>
                    </span>
                    <span v-else class="checkbox-type1">
                        <input v-model="selectedItems" :id="item.id" type="checkbox" :value="item.id">
                        <label :for="item.id">체크</label>
                    </span>
                </td>
                <td>{{ item.id }}</td>
                <td>
                  <span class="badge" :style="{ backgroundColor: getContentTypeColor(item.contentType) }">
                    {{ getContentTypeLabel(item.contentType) }}
                  </span>
                </td>
                <td>
                  <router-link :to="{ name: 'MonthlyContentDetail', params: { id: item.id } }" class="subject">{{ item.selectedMonth }}</router-link>
                </td>
                <td>
                  {{ item.contentId }}
                  <a v-if="item.contentId && item.artistUserAccountId"
                     :href="`https://article21.kr/artist/${item.artistUserAccountId}`"
                     target="_blank">
                    [바로가기]
                  </a>
                </td>
                <td>{{ item.sortOrder }}</td>
                <td>{{ item.createdBy }}</td>
                <td>{{ item.createdAt }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue';
import {apiClient} from '@/services/auth-header';
import router from '@/router';
import {
  getArticleTypeLabel,
  getArticleTypeColor,
  getArticleTypeOptions,
} from '@/constants/articleType';
import {useDragReorder} from '@/composables/useDragReorder';

const items = ref([]);
const selectedItems = ref([]);
const searchQuery = ref('');
const filterContentType = ref('');
const visibilityFilter = ref(''); // '' 전체 | 'public' 이번달 | 'private' 그 외
const API_URL = '/contents';

const {overIndex, onDragStart, onDragEnter, onDragEnd, onDrop} = useDragReorder();

// 현재 날짜 가져오기
const today = new Date();
const selectedYear = ref(today.getFullYear());
const selectedMonth = ref(today.getMonth() + 1);
const formattedSelectedMonth = computed(() => {
  return selectedYear.value && selectedMonth.value
      ? `${selectedYear.value}.${String(selectedMonth.value).padStart(2, '0')}`
      : '';
});

const contentTypeOptions = computed(() => getArticleTypeOptions());

const getContentTypeLabel = getArticleTypeLabel;
const getContentTypeColor = getArticleTypeColor;

// 드래그 재정렬의 그룹 키 = 콘텐츠 타입 (순서는 (이번달, 타입) 안에서만 의미)
const groupOf = (item) => item.contentType;

// 공개 여부 필터 적용 (공개 = 선정월이 이번 달). 공개면 같은 타입끼리 인접하도록 정렬.
const displayItems = computed(() => {
  if (visibilityFilter.value === 'public') {
    return items.value
      .filter((it) => it.selectedMonth === formattedSelectedMonth.value)
      .slice()
      .sort((a, b) => a.contentType.localeCompare(b.contentType) || a.sortOrder - b.sortOrder);
  }
  if (visibilityFilter.value === 'private') {
    return items.value.filter((it) => it.selectedMonth !== formattedSelectedMonth.value);
  }
  return items.value;
});

// 드래그 활성화 — 공개(이번 달) + 특정 타입 선택 시. 단일 그룹만 표시되어 명확.
const isDraggable = computed(
  () => visibilityFilter.value === 'public' && filterContentType.value !== ''
);

// 타입이 바뀌는 첫 행 표시(그룹 구분선)
const isGroupStart = (item, index) =>
  isDraggable.value &&
  index > 0 &&
  displayItems.value[index - 1].contentType !== item.contentType;

// 드롭 시: 영향받은 타입 그룹만 표시 순서대로 sortOrder 0..N 재부여 후 저장
const applyGroupOrder = async (groupRows) => {
  const payload = groupRows.map((item, idx) => ({id: item.id, sortOrder: idx}));
  try {
    await apiClient.put(API_URL + '/monthly-contents/order-bulk', payload);
    await fetchItems();
  } catch (error) {
    console.error('순서 변경 실패', error);
    alert('순서 변경 중 오류가 발생했습니다.');
    await fetchItems();
  }
};

const toggleAllCheck = (event) => {
  if (event.target.checked) {
    selectedItems.value = items.value.map(item => item.id);
  } else {
    selectedItems.value = [];
  }
};

const addContent = () => {
  router.push({
    name: 'MonthlyContentCreate'
  });
};

const fetchItems = async () => {
  try {
    const params = {q: searchQuery.value};
    if (filterContentType.value) {
      params.contentType = filterContentType.value;
    }
    const response = await apiClient.get(API_URL + '/monthly-contents', {params});
    items.value = response.data;
  } catch (error) {
    console.error('Error fetching data:', error);
  }
};

const deleteItems = async () => {
  if (confirm('삭제 하시겠습니까?')) {
    try {
      const response = await apiClient.delete(API_URL + '/monthly-contents', {data: selectedItems.value});
      if (response.status === 200) {
        alert('삭제되었습니다.')
        selectedItems.value = [];
        await fetchItems();
      }
    } catch (error) {
      console.error('Error deleting items:', error);
    }
  }
};

onMounted(async () => {
  await fetchItems();
});

</script>

<style scoped>
.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 3px;
  color: white;
  font-weight: 600;
  font-size: 11px;
  white-space: nowrap;
}

select {
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  max-width: 200px;
}

.reorder-hint {
  margin-top: 24px;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  background: #f3f4f6;
  color: #6b7280;
}
.reorder-hint--on {
  background: #eef2ff;
  color: #4338ca;
  font-weight: 600;
}
.reorder-hint b {
  color: #101733;
}
.drag-row:hover {
  background: #f8faff;
}
.drag-over td {
  border-top: 2px solid #4f46e5;
}
.group-start td {
  border-top: 2px solid #d1d5db;
}
.drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  padding: 3px 5px;
  border-radius: 4px;
  background: #eef2ff;
  color: #4f46e5;
  cursor: grab;
  user-select: none;
  vertical-align: middle;
}
.drag-handle:active {
  cursor: grabbing;
}
</style>
